import React, { useEffect, useMemo, useState } from "react";
import { HardHat, Plus, Search, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

// Admin panel: pick a delivery man, switch INDANE agencies ON/OFF (auto-save).
export function DeliveryMenPanel({ onClose, onChanged }) {
  const [data, setData] = useState(null);
  const reload = () => api.get("/pending-deliveries", { params: { date: "all", company: "INDANE", ignore_enabled: true } })
    .then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const men = data?.delivery_men || [];
  const groups = data?.indane_groups || [];
  const [selId, setSelId] = useState(men[0]?.id || "");
  const [local, setLocal] = useState({});
  const [q, setQ] = useState("");
  const [show, setShow] = useState("all");
  const [saving, setSaving] = useState("");
  const [creating, setCreating] = useState(false);
  const sel = men.find((m) => m.id === selId);
  const ids = local[selId] ?? sel?.delivery_agency_ids ?? [];

  useEffect(() => {
    if (!selId && men[0]) setSelId(men[0].id);
  }, [men, selId]);

  const shown = useMemo(() => groups.filter((g) => {
    if (q && !g.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (show === "on") return ids.includes(g.id);
    if (show === "off") return !ids.includes(g.id);
    return true;
  }), [groups, q, show, ids]);

  const save = async (next, key) => {
    setLocal((l) => ({ ...l, [selId]: next }));
    setSaving(key);
    try {
      await api.patch(`/admin/users/${selId}`, { delivery_agency_ids: next });
      toast.success(`✓ Saved · ${sel.display_name} · ${next.length} agencies ON`);
      reload();
      onChanged();
    } catch (e) {
      setLocal((l) => ({ ...l, [selId]: ids }));
      toast.error(formatApiError(e));
    } finally {
      setSaving("");
    }
  };
  const toggle = (gid) => save(ids.includes(gid) ? ids.filter((x) => x !== gid) : [...ids, gid], gid);
  const bulk = (on) => {
    const vis = shown.map((g) => g.id);
    save(on ? [...new Set([...ids, ...vis])] : ids.filter((x) => !vis.includes(x)), on ? "all-on" : "all-off");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="dm-panel">
        <div className="px-4 py-3 bg-[#385C4B] text-white flex items-center gap-2">
          <HardHat className="w-5 h-5" />
          <div className="font-bold">Delivery Men</div>
          <span className="text-[10px] font-mono bg-white/15 px-1.5 py-0.5 rounded">INDANE ONLY</span>
          <button onClick={() => setCreating((v) => !v)} className="ml-auto text-xs font-semibold px-2.5 py-1.5 rounded bg-white text-[#385C4B] flex items-center gap-1" data-testid="dm-new-toggle">
            <Plus className="w-3.5 h-3.5" />New Delivery Man
          </button>
          <button onClick={onClose} className="p-1" data-testid="dm-panel-close"><X className="w-5 h-5" /></button>
        </div>
        {creating && <NewDeliveryMan onCreated={(u) => { setCreating(false); setSelId(u.id); reload(); onChanged(); }} />}
        {!data ? (
          <div className="p-6 text-sm text-[#6B6B61]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : men.length === 0 ? (
          <div className="p-6 text-sm text-[#6B6B61]" data-testid="dm-empty">Abhi koi delivery man nahi hai. Upar "New Delivery Man" se banao.</div>
        ) : (
          <>
            <div className="p-3 space-y-2 border-b border-[#EDEDE5]">
              <div className="flex items-center gap-2">
                <select value={selId} onChange={(e) => setSelId(e.target.value)} data-testid="dm-select"
                  className="flex-1 px-2 py-2 text-sm border border-[#DCDCD3] rounded bg-white">
                  {men.map((m) => <option key={m.id} value={m.id}>{m.display_name} · @{m.username}</option>)}
                </select>
                <span className="text-xs font-mono text-[#385C4B] whitespace-nowrap" data-testid="dm-on-count">{ids.length} ON</span>
              </div>
              <div className="text-[11px] font-mono text-[#6B6B61]" data-testid="dm-today-done">Aaj Done: <b className="text-emerald-700">{sel?.today_done || 0}</b></div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#6B6B61]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agency…" data-testid="dm-search"
                  className="w-full pl-8 pr-2 py-2 text-sm border border-[#DCDCD3] rounded" />
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-mono uppercase text-[#6B6B61]">Show:</span>
                {[["all", "All"], ["on", "ON"], ["off", "OFF"]].map(([k, l]) => (
                  <button key={k} onClick={() => setShow(k)} data-testid={`dm-show-${k}`}
                    className={`px-2.5 py-1 text-[11px] font-mono rounded border ${show === k ? "bg-[#385C4B] text-white border-[#385C4B]" : "border-[#DCDCD3]"}`}>{l}</button>
                ))}
                <button onClick={() => bulk(true)} disabled={!!saving} data-testid="dm-all-on" className="ml-auto px-2.5 py-1 text-[11px] font-mono rounded border border-[#385C4B] text-[#385C4B]">Sab ON</button>
                <button onClick={() => bulk(false)} disabled={!!saving} data-testid="dm-all-off" className="px-2.5 py-1 text-[11px] font-mono rounded border border-[#C8553D] text-[#C8553D]">Sab OFF</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-[#F0F0EA]" data-testid="dm-agency-list">
              {shown.map((g) => {
                const on = ids.includes(g.id);
                const others = g.delivery_men.filter((d) => d.id !== selId);
                return (
                  <div key={g.id} className="px-3 py-2.5 flex items-center gap-2" data-testid={`dm-row-${g.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{g.name}</div>
                      <div className="text-[11px] font-mono text-[#6B6B61]">
                        {g.pending} pending{others.length ? ` · 👷 ${others.map((d) => d.display_name).join(", ")}` : ""}
                      </div>
                    </div>
                    {saving === g.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#6B6B61]" />}
                    <button onClick={() => toggle(g.id)} disabled={!!saving} role="switch" aria-checked={on} data-testid={`dm-switch-${g.id}`}
                      className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${on ? "bg-[#385C4B]" : "bg-[#DCDCD3]"}`}>
                      <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${on ? "translate-x-7" : "translate-x-0.5"}`} />
                      <span className={`absolute inset-0 flex items-center text-[9px] font-bold ${on ? "justify-start pl-1.5 text-white" : "justify-end pr-1.5 text-[#6B6B61]"}`}>{on ? "ON" : "OFF"}</span>
                    </button>
                  </div>
                );
              })}
              {shown.length === 0 && <div className="p-4 text-sm text-[#6B6B61]">Koi agency nahi mili.</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NewDeliveryMan({ onCreated }) {
  const [f, setF] = useState({ display_name: "", username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", {
        ...f, username: f.username.trim().toLowerCase(), display_name: f.display_name.trim(),
        role: "delivery_man", delivery_agency_ids: [],
      });
      toast.success(`Delivery man bana: ${data.display_name}. Ab agencies ON karo.`);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const cls = "px-2 py-2 text-sm border border-[#DCDCD3] rounded flex-1 min-w-[120px]";
  return (
    <form onSubmit={submit} className="p-3 bg-[#F5F5F0] border-b border-[#EDEDE5] flex flex-wrap gap-2" data-testid="dm-new-form">
      <input required value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} placeholder="Naam (e.g. RAJU DELIVERY)" className={cls} data-testid="dm-new-name" />
      <input required minLength={3} value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} placeholder="Username (user id)" className={cls} data-testid="dm-new-username" />
      <input required minLength={6} type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password (min 6)" className={cls} data-testid="dm-new-password" />
      <button disabled={busy} className="px-3 py-2 text-sm font-semibold rounded bg-[#385C4B] text-white flex items-center gap-1 disabled:opacity-50" data-testid="dm-new-submit">
        <Check className="w-4 h-4" />{busy ? "Creating…" : "Create"}
      </button>
    </form>
  );
}
